import React, { useState, useEffect } from 'react'
import { Head, Link } from '@inertiajs/react'
import { Button } from '@/components/ui/button'
import { ArrowLeft } from 'lucide-react'
import DashboardLayout from '@/frontend/layouts/DashboardLayout'
import SelectionView from './requests/SelectionView'
import DiyView from './requests/DiyView'
import BrowserUseView from './requests/BrowserUseView'
import RequestForm from './requests/RequestForm'
import type { DiyWorker } from './types'

interface Props {
  page_title: string
  digital_workers: DiyWorker[]
  diy_step?: number | null
  doc_url?: string | null
  credential_name?: string | null
  credential_id?: string | null
}

type View = 'selection' | 'diy' | 'browser' | 'form'

const Requests = ({ page_title, digital_workers, diy_step, doc_url, credential_name, credential_id }: Props) => {
  const [view, setView] = useState<View>(() => diy_step ? 'diy' : 'selection')
  const [diyState, setDiyState] = useState({
    step: diy_step || 1,
    docUrl: doc_url || '',
    credentialName: credential_name || '',
    credentialId: credential_id || '',
  })

  useEffect(() => {
    if (diy_step) {
      setView('diy')
      setDiyState({ step: diy_step, docUrl: doc_url || '', credentialName: credential_name || '', credentialId: credential_id || '' })
    }
  }, [diy_step, doc_url, credential_name, credential_id])

  return (
    <>
      <Head title={page_title} />
      <div className="px-4 py-6">
        <div className="space-y-6">
          <div className="flex items-center gap-3">
            {view === 'selection' ? (
              <Button asChild variant="ghost" size="sm">
                <Link href="/dashboard/integrations">
                  <ArrowLeft className="h-4 w-4" /> Integrações
                </Link>
              </Button>
            ) : (
              <Button variant="ghost" size="sm" onClick={() => setView('selection')}>
                <ArrowLeft className="h-4 w-4" /> Voltar
              </Button>
            )}
          </div>

          <div className="text-center py-4">
            <h1 className="text-xl font-semibold tracking-tight text-foreground">
              {view === 'diy' ? 'Integração via API' : view === 'browser' ? 'Integração via navegador' : view === 'form' ? 'Solicitar integração' : page_title}
            </h1>
            <p className="text-muted-foreground text-sm mt-1">
              {view === 'diy'
                ? 'Como integrar qualquer ferramenta com seu agente via API'
                : view === 'browser'
                  ? 'Como integrar qualquer ferramenta usando o navegador'
                  : view === 'form'
                    ? 'Preencha os dados da integração que você precisa'
                    : 'Escolha como você gostaria de integrar uma nova ferramenta'}
            </p>
          </div>

          {view === 'selection' && (
            <SelectionView onSelectDiy={() => setView('diy')} onSelectBrowser={() => setView('browser')} onSelectRequest={() => setView('form')} />
          )}
          {view === 'diy' && (
            <DiyView
              key={`${diyState.step}-${diyState.credentialName}`}
              workers={digital_workers}
              initialStep={diyState.step}
              initialDocUrl={diyState.docUrl}
              initialCredentialName={diyState.credentialName}
              initialCredentialId={diyState.credentialId}
            />
          )}
          {view === 'browser' && <BrowserUseView />}
          {view === 'form' && <RequestForm />}
        </div>
      </div>
    </>
  )
}

Requests.layout = (page: React.ReactElement) => <DashboardLayout>{page}</DashboardLayout>

export default Requests
